import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import {
  inquirySubjects,
  inquiryTimings,
  inquiryAssurances,
  organizationTypes,
  teamSizes,
  consultation5,
} from "@/brand/content";
import { FormField } from "@/components/form/FormField";
import { FormSelect } from "@/components/form/FormSelect";
import { FormTextarea } from "@/components/form/FormTextarea";

/**
 * ConsultationSection5 — Deep Mineral Blue chamber.
 *
 * The one accent chamber. Consultation-led conversion: the website's
 * "job" resolves here.
 *
 * On this dark surface the form fields invert: labels become brass on
 * ivory, inputs are transparent with brass bottom border, submit button
 * is Restrained Brass filled.
 */
export function ConsultationSection5() {
  return (
    <section
      id="contact"
      data-testid="consultation"
      style={{
        background: C.surfacePrimary,
        color: C.textOnDark,
      }}
    >
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Intro rail */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-14"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                }}
              >
                {consultation5.eyebrow}
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnDark,
                fontSize: "clamp(2.2rem, 4vw, 3.4rem)",
                fontWeight: 400,
                lineHeight: 1.02,
                letterSpacing: "-0.012em",
              }}
            >
              Tell us about
              <br />
              the moment your
              <br />
              organization is in.
            </h2>
            <p
              className="mt-8 font-inter leading-[1.7] max-w-[42ch]"
              style={{ color: C.whiteOnDark, fontSize: "1.02rem" }}
            >
              {consultation5.body}
            </p>

            <dl className="mt-12 space-y-0">
              {inquiryAssurances.map(([t, b], i) => (
                <div
                  key={t}
                  className="py-5 flex items-baseline gap-6"
                  style={{
                    borderTop:
                      i === 0 ? `1px solid ${C.brassSoft}` : "none",
                    borderBottom: `1px solid ${C.brassSoft}`,
                  }}
                >
                  <span
                    className="font-inter text-[10px] uppercase font-bold tabular-nums shrink-0"
                    style={{
                      color: C.accentMetal,
                      letterSpacing: "0.28em",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <dt
                      className="font-inter text-[10.5px] uppercase font-semibold mb-1.5"
                      style={{
                        color: C.accentMetal,
                        letterSpacing: "0.24em",
                      }}
                    >
                      {t}
                    </dt>
                    <dd
                      className="font-inter leading-[1.6]"
                      style={{ color: C.whiteOnDark, fontSize: "0.95rem" }}
                    >
                      {b}
                    </dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>

          {/* Form rail */}
          <div className="lg:col-span-7">
            <div
              className="p-8 lg:p-10"
              style={{
                background: C.surfaceLight2,
                color: C.textOnLight,
                border: `1px solid ${C.brassLine}`,
              }}
            >
              <form
                data-testid="inquiry-form"
                onSubmit={(e) => e.preventDefault()}
                className="space-y-8"
              >
                <p
                  className="font-inter text-[10px] uppercase font-semibold pb-4"
                  style={{
                    color: C.accentMetal,
                    letterSpacing: "0.28em",
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                >
                  Institutional intake &nbsp;·&nbsp; 9 fields
                </p>

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                  <FormField
                    label="Full name"
                    name="name"
                    required
                    index="01"
                    testId="field-name"
                  />
                  <FormField
                    label="Organization"
                    name="organization"
                    required
                    index="02"
                    testId="field-organization"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                  <FormField
                    label="Role / title"
                    name="role"
                    required
                    index="03"
                    testId="field-role"
                  />
                  <FormField
                    label="Email"
                    name="email"
                    type="email"
                    required
                    index="04"
                    testId="field-email"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                  <FormSelect
                    label="Organization type"
                    name="organizationType"
                    options={organizationTypes}
                    required
                    index="05"
                    testId="field-organization-type"
                  />
                  <FormSelect
                    label="Team size"
                    name="teamSize"
                    options={teamSizes}
                    required
                    index="06"
                    testId="field-team-size"
                  />
                </div>

                <FormSelect
                  label="Challenge area — what you’re seeking support with"
                  name="subject"
                  options={inquirySubjects}
                  required
                  index="07"
                  testId="field-subject"
                />

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                  <FormSelect
                    label="Timeline"
                    name="timing"
                    options={inquiryTimings}
                    required
                    index="08"
                    testId="field-timing"
                  />
                  <div className="hidden sm:block" aria-hidden="true" />
                </div>

                <FormTextarea
                  label="A brief description of the moment"
                  name="challenge"
                  hint="Two or three sentences is enough. Please don’t include sensitive incident details here — we’ll surface those carefully in the discovery conversation."
                  required
                  index="09"
                  testId="field-challenge"
                />

                <p
                  className="font-inter text-[12px] leading-[1.6] max-w-[60ch]"
                  style={{ color: C.inkOnLightSoft }}
                >
                  By submitting, you confirm you’re reaching out on behalf of
                  your organization. Every inquiry is treated confidentially.
                </p>

                <div
                  className="pt-6 flex flex-col sm:flex-row sm:items-center gap-6"
                  style={{ borderTop: `1px solid ${C.borderSoft}` }}
                >
                  <button
                    type="submit"
                    data-testid="inquiry-submit"
                    className="group inline-flex items-center justify-between gap-4 px-7 py-4 font-inter text-[12px] uppercase font-semibold transition-colors"
                    style={{
                      background: C.accentMetal,
                      color: C.bgDark,
                      letterSpacing: "0.24em",
                      border: `1px solid ${C.accentMetal}`,
                      minWidth: 240,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = C.bgDark;
                      e.currentTarget.style.borderColor = C.bgDark;
                      e.currentTarget.style.color = C.textOnDark;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = C.accentMetal;
                      e.currentTarget.style.borderColor = C.accentMetal;
                      e.currentTarget.style.color = C.bgDark;
                    }}
                  >
                    Submit inquiry
                    <ArrowRight
                      size={14}
                      weight="bold"
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </button>
                  <span
                    className="font-inter text-[10.5px] uppercase font-semibold"
                    style={{
                      color: C.inkOnLightSoft,
                      letterSpacing: "0.26em",
                    }}
                  >
                    Response within 2 business days
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
